/* assets/vc-transition.js — the lace veil between a noir page and a sweet page (spec D14; task-16-brief.md).
 *
 * The contract, end to end:
 *   1. A same-origin click on an `a[data-mood]` whose mood is not the page's (cards, the turntable's chapter link, the header's colourway
 *      swatches, the chapter index, the set and look links) is taken over: `veilTarget` (assets/vc-logic.js) says so, and it leaves to the
 *      browser a modifier-click, target=_blank, a download, a # link, another origin, reduced motion, and a click that something already
 *      prevented. We preventDefault, the veil comes down over the page (DESCEND_MS), the target mood goes into sessionStorage['vc-veil'],
 *      and `location.assign(href)` follows the link.
 *   2. The page after it starts covered: the inline <head> script in layout/theme.liquid sees the key before the first paint and adds
 *      html.vc-veiled and html[data-vc-veil=<mood>]; assets/vc-theme.css draws a sheet of that mood's tulle at once. This module then
 *      builds the veil in the covered pose over the sheet.
 *   3. At DOMContentLoaded plus two frames (the first paint) the veil lifts (LIFT_MS); then the root, the class, the attributes and the
 *      key go.
 *   4. Never stuck: a timer removes it all FAILSAFE_MS after this module ran, whatever the lift did (the head script has a last-resort
 *      timer of its own for a module that never runs); a page restored from the back/forward cache lifts at once (`pageshow`, persisted);
 *      a departure whose navigation never happens (a refused beforeunload prompt, a link that stays on the page) is undone after
 *      STUCK_MS (3 s; batch review 1, M1); a veil that cannot be built lets the link work as it would have. With JS off nothing here
 *      exists. A later veil-able click while a departure is under way is not swallowed (M1): the departure restarts to the new link
 *      (`retarget`); the same link twice (a double click) changes nothing.
 *   The turntable's chapter link (assets/vc-turntable.js `cta`): its own listener, on the link, prevents the FIRST click and plays a 1.3 s
 *   push-in, then `follow()` clicks the same link again for real. Ours is on `document`, so it runs after the link's (the bubble phase):
 *   the first click arrives prevented and `veilTarget` ignores it, the second (not prevented, `isTrusted` false, button 0, no modifiers)
 *   is veiled like any other. A capture-phase listener would take the first click and skip the push-in.
 *
 * Ported from prototypes/lace.html (script :328-910; veil styles :153-177), function by function: the easing curves (:346-368), the
 * seeded PRNG (:371), the drawing primitives spline / pol / rose / leafShape (:380-426), the pre-rasterised fold and sheen tiles
 * (:434-467), the bobbinet pattern (:469-476), the front veil = the tulle body plus one scalloped lace hem (buildFront :495-621), the plain
 * back tulle (buildBack :636-652) and the per-frame paint of the two veils (:696-711). The geometry, the seeds and the drawing are the
 * prototype's. Changed:
 *   - The stage is the window: the veil is fixed, as wide as the window plus 24px each side, as tall as the window, and it is built when
 *     it is needed (a click, or the arrival of a veiled page) for the ONE palette that is needed: ivory tulle for a sweet target, black
 *     for a noir one (:161-162). So only that palette's two fold tiles are drawn (from one noise field, not four).
 *   - The prototype's 2200 ms (the swap happens under the gathered veil) is cut where a page load replaces it: the DESCENT is the first
 *     half up to the front lace landing (:678: back tulle 0-820, front 80-900) and the LIFT the second (front 1160-1880, back 1280-1960,
 *     counted from 1160), each scaled to the brief's 650 ms with the offset between the two veils kept. The outgoing words do not
 *     withdraw and the incoming ones are not staged: the page underneath is a real page.
 *   - Class names are vc- (vcv- inside the drawing) and the ids vcvF* / vcvB*; the palette is the root's class (`vc-veil--ivory`, `--black`),
 *     its colours are custom properties in assets/vc-theme.css.
 *   - The veil is one at a time, so its SVG ids are fixed (CLAUDE.md "No SVG ids in a snippet rendered twice").
 * Web APIs: sessionStorage https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage; pageshow / persisted
 * https://developer.mozilla.org/en-US/docs/Web/API/Window/pageshow_event; location.assign
 * https://developer.mozilla.org/en-US/docs/Web/API/Location/assign.
 */
// '@vc/logic': the import map's name (snippets/scripts.liquid) for the VERSIONED assets/vc-logic.js (guard: tests/js/import-map.test.mjs).
import { veilTarget } from '@vc/logic';

const KEY = 'vc-veil';          // sessionStorage: the mood the page being left is taking the visitor to; layout/theme.liquid reads it in <head>
const DESCEND_MS = 650;         // brief: "about 650 ms"
const LIFT_MS = 650;
const FAILSAFE_MS = 1500;       // brief: the veil is gone 1500 ms after the page loaded, no matter what
const STUCK_MS = 3000;          // a departure that has not left the page by then is undone (an aborted or refused navigation; was 8000)

/* ---- easing and small helpers, lace.html:346-374 ---- */
/* cubic-bezier easing, the same curve definition as CSS cubic-bezier() (https://www.w3.org/TR/css-easing-1/#cubic-bezier-easing-functions),
   solved for x by Newton iteration with a bisection fallback. */
function bezier(x1, y1, x2, y2) {
  const a = (p1, p2) => 1 - 3 * p2 + 3 * p1;
  const b = (p1, p2) => 3 * p2 - 6 * p1;
  const c = (p1) => 3 * p1;
  const at = (t, p1, p2) => ((a(p1, p2) * t + b(p1, p2)) * t + c(p1)) * t;
  const slope = (t, p1, p2) => 3 * a(p1, p2) * t * t + 2 * b(p1, p2) * t + c(p1);
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const s = slope(t, x1, x2);
      if (Math.abs(s) < 1e-6) break;
      const e = at(t, x1, x2) - x;
      if (Math.abs(e) < 1e-6) return at(t, y1, y2);
      t -= e / s;
    }
    let lo = 0;
    let hi = 1;
    t = x;
    for (let i = 0; i < 30; i++) {
      const v = at(t, x1, x2);
      if (Math.abs(v - x) < 1e-6) break;
      if (v < x) lo = t; else hi = t;
      t = (lo + hi) / 2;
    }
    return at(t, y1, y2);
  };
}
const easeDown = bezier(.55, 0, .15, 1);   // decelerates into place: the lace is readable as it lands
const easeUp = bezier(.6, 0, .2, 1);
/* seeded PRNG (LCG, Numerical Recipes constants) so the hand-made irregularity is stable */
const rng = (seed) => () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
const f1 = (n) => Math.round(n * 10) / 10;
const f2 = (n) => Math.round(n * 100) / 100;
const seg = (t, a, b) => Math.min(1, Math.max(0, (t - a) / (b - a)));

/* ---- drawing primitives, lace.html:379-426 ---- */
// Catmull-Rom through points -> cubic Béziers (uniform, tension 1/6)
function spline(pts, closed) {
  const n = pts.length;
  const last = closed ? n : n - 1;
  let d = 'M' + f2(pts[0][0]) + ' ' + f2(pts[0][1]);
  for (let i = 0; i < last; i++) {
    let p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    let p3 = pts[(i + 2) % n];
    if (!closed) { if (i === 0) p0 = p1; if (i + 2 >= n) p3 = p2; }
    d += ' C' + f2(p1[0] + (p2[0] - p0[0]) / 6) + ' ' + f2(p1[1] + (p2[1] - p0[1]) / 6) + ' ' +
      f2(p2[0] - (p3[0] - p1[0]) / 6) + ' ' + f2(p2[1] - (p3[1] - p1[1]) / 6) + ' ' + f2(p2[0]) + ' ' + f2(p2[1]);
  }
  return d + (closed ? 'Z' : '');
}
// polar, angle in degrees measured clockwise from "up"
function pol(cx, cy, r, deg) { const a = (deg - 90) * Math.PI / 180; return [cx + r * Math.cos(a), cy + r * Math.sin(a)]; }

/* A lace rose: five heart-notched outer petals overlapping by 8°, three cupped inner petals, a rolled lip on each outer petal, a
   spiral heart. */
function rose(cx, cy, r, rot, R) {
  const jit = (a) => (R ? (R() - .5) * 2 * a : 0);
  const out = { outer: [], inner: [], lip: [], spiral: '' };
  const petal = (ri, ro, c, h, notch) => spline([pol(cx, cy, ri, c - h * .72), pol(cx, cy, ro * .8, c - h), pol(cx, cy, ro, c - h * .5),
    pol(cx, cy, ro * (1 - notch), c), pol(cx, cy, ro, c + h * .5), pol(cx, cy, ro * .8, c + h),
    pol(cx, cy, ri, c + h * .72), pol(cx, cy, ri * .85, c)], true);
  for (let k = 0; k < 5; k++) {
    const c = rot + k * 72 + jit(6);
    const ro = r * (1 + jit(.05));
    out.outer.push(petal(r * .4, ro, c, 40, .09));
    out.lip.push(spline([pol(cx, cy, ro * .74, c - 30), pol(cx, cy, ro * .83, c - 12), pol(cx, cy, ro * .8, c + 6)], false));
  }
  for (let k = 0; k < 3; k++) { const c = rot + 36 + k * 120 + jit(8); out.inner.push(petal(r * .1, r * .56, c, 64, .05)); }
  const sp = [];
  const turns = 3.1 * Math.PI;
  for (let th = 0; th <= turns + .01; th += .3) sp.push(pol(cx, cy, r * .05 + r * .21 * th / turns, rot + 20 + th * 180 / Math.PI));
  out.spiral = spline(sp, false);
  return out;
}
/* A leaf on a gently bent midrib. ang: screen degrees (0 = right, 90 = down). */
function leafShape(bx, by, ang, len, wid, bend) {
  const a = ang * Math.PI / 180;
  const ux = Math.cos(a);
  const uy = Math.sin(a);
  const nx = -uy;
  const ny = ux;
  const mid = (t) => { const b = bend * len * Math.sin(Math.PI * t); return [bx + ux * len * t + nx * b, by + uy * len * t + ny * b]; };
  const L = [];
  const Rr = [];
  [.12, .3, .5, .7, .86].forEach((t) => {
    const m = mid(t);
    const w = wid * Math.pow(Math.sin(Math.PI * Math.pow(t, .8)), .85);
    L.push([m[0] + nx * w, m[1] + ny * w]); Rr.push([m[0] - nx * w, m[1] - ny * w]);
  });
  const pts = [mid(0)].concat(L, [mid(1)], Rr.reverse());
  const rib = [mid(0), mid(.3), mid(.6), mid(.84)];
  return { o: spline(pts, true), m: spline(rib, false), tip: mid(1) };
}

/* ---- fold noise, lace.html:428-467: 512x64 tiles of 1-D value noise with a slow vertical drift, stretched over the veil instead of a live
   full-size feTurbulence. Density streaks = more thread where the tulle folds; sheen = a thin highlight along each fold crest. ---- */
let NOISE = null;
function noiseField() {
  if (NOISE) return NOISE;
  const R = rng(4117);
  const oct = [[88, .6], [31, .28], [11, .12]];
  const lat = oct.map((o) => Array.from({ length: Math.ceil(600 / o[0]) + 3 }, () => R()));
  const noise = (x) => {
    let s = 0;
    oct.forEach((o, k) => {
      const u = x / o[0] + 1;
      const i = Math.floor(u);
      const f = u - i;
      const w = (1 - Math.cos(Math.PI * f)) / 2;
      s += (lat[k][i] * (1 - w) + lat[k][i + 1] * w) * o[1];
    });
    return s;
  };
  NOISE = new Float32Array(512 * 64);
  for (let y = 0; y < 64; y++) {
    const sh = 7 * Math.sin(2 * Math.PI * y / 64);
    for (let x = 0; x < 512; x++) NOISE[y * 512 + x] = noise(x + sh);
  }
  return NOISE;
}
function tile(rgb, fn) {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 64;
  const g = c.getContext('2d');
  const im = g.createImageData(512, 64);
  const d = im.data;
  const N = noiseField();
  for (let i = 0; i < 512 * 64; i++) {
    d[i * 4] = rgb[0]; d[i * 4 + 1] = rgb[1]; d[i * 4 + 2] = rgb[2];
    d[i * 4 + 3] = Math.round(255 * Math.max(0, Math.min(1, fn(N[i]))));
  }
  g.putImageData(im, 0, 0);
  return c.toDataURL('image/png');
}
const dens = (n) => Math.pow(Math.max(0, (n - .36) * 1.9), 1.4);
const TILES = {};
function foldTiles(pal) {
  if (!TILES[pal]) {
    TILES[pal] = pal === 'ivory'
      ? { fold: tile([251, 246, 240], dens), sheen: tile([255, 255, 255], (n) => Math.exp(-Math.pow((n - .66) / .009, 2))) }
      // soft, wide: silk, not rods
      : { fold: tile([4, 2, 3], dens), sheen: tile([184, 155, 106], (n) => Math.exp(-Math.pow((n - .62) / .06, 2))) };
  }
  return TILES[pal];
}

function netPattern(id, w, h, sw) {
  // honeycomb tile (pointy-top hexagons): the structure of bobbinet tulle
  const s = h / 3;
  const q = w / 2;
  return '<pattern id="' + id + '" width="' + w + '" height="' + h + '" patternUnits="userSpaceOnUse">' +
    '<path class="vcv-t-line" stroke-width="' + sw + '" d="M0 ' + f1(s / 2) + ' L' + q + ' 0 L' + w + ' ' + f1(s / 2) +
    ' M0 ' + f1(s / 2) + ' V' + f1(1.5 * s) + ' L' + q + ' ' + f1(2 * s) + ' L' + w + ' ' + f1(1.5 * s) + ' V' + f1(s / 2) +
    ' M' + q + ' ' + f1(2 * s) + ' V' + h + '"/></pattern>';
}
function foldImages(WS, TT, pal) {
  const t = foldTiles(pal);
  const im = (cls, href) => '<image class="' + cls + '" href="' + href + '" x="0" y="0" width="' + WS + '" height="' + TT + '" preserveAspectRatio="none"/>';
  return im('vcv-fold', t.fold) + im('vcv-sheen', t.sheen);
}
function svgOpen(cls, WS, TT) {
  return '<svg class="' + cls + '" xmlns="http://www.w3.org/2000/svg" width="' + WS + '" height="' + f1(TT) + '" viewBox="0 0 ' + WS + ' ' + f1(TT) + '">';
}

function spline0(f, W) { let d = ''; for (let x = 1; x < W; x += 1) d += ' L' + x + ' ' + f1(f(x)); return d + ' L' + f1(W) + ' ' + f1(f(W)); }
function offsetPath(f, W, dy) { let d = 'M0 ' + f1(f(0) + dy); for (let x = 1; x < W; x += 1) d += ' L' + x + ' ' + f1(f(x) + dy); return d + ' L' + f1(W) + ' ' + f1(f(W) + dy); }
// points spaced by arc length along y = f(x), with the unit normal
function alongCurve(f, x0, x1, spacing, cb) {
  let acc = spacing * .5;
  let px = x0;
  let py = f(x0);
  for (let x = x0 + .25; x <= x1; x += .25) {
    const y = f(x);
    acc += Math.hypot(x - px, y - py);
    if (acc >= spacing) {
      acc -= spacing;
      const dx = x - px;
      const dyy = y - py;
      const L = Math.hypot(dx, dyy) || 1;
      cb(x, y, -dyy / L, dx / L);
    }
    px = x; py = y;
  }
}

/* ---- FRONT VEIL, lace.html:487-621: tulle body + one scalloped lace hem. Hem (band-local units, drawn 1:1 then scaled K): a festooned edge
   with picots, a heavy cordonnet just inside, a row of eyelets, a fine net ground, and in each scallop a floral spray (open rose, three
   leaves, a stem with a bud, a tendril), mirrored on alternate scallops and jittered with rng(seed) so the repeat is not wallpaper. ---- */
function buildFront(WS, H, pal) {
  const p = 'vcvF';
  const K = 1.2;
  const W = WS / K;
  const n = Math.max(2, Math.round(W / 190));
  const P = W / n;
  const DEP = 74;
  const AMP = 40;
  const B = DEP + AMP + 6;
  const outer = (x) => { const u = (((x % P) + P) % P) / P; return DEP + AMP * Math.pow(Math.sin(Math.PI * u), .8) + 3.4 * Math.pow(Math.abs(Math.sin(7 * Math.PI * u)), .6); };
  const TT = H + B * K;
  const R = rng(20260926);

  let silD = 'M0 -2 L' + WS + ' -2';
  for (let sx = WS; sx >= 0; sx -= 1) silD += ' L' + sx + ' ' + f1(H - 1 + K * outer(sx / K));
  silD += 'Z';

  /* fill layer: its opacity is driven per frame (sheer while travelling, gathered at the swap). It thins across the top of the lace band
     so the tulle runs on into the sheer lace ground. */
  const fill = svgOpen('vcv-tulle', WS, TT) + '<defs><clipPath id="' + p + 'fc"><path d="' + silD + '"/></clipPath>' +
    '<linearGradient id="' + p + 'fg" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="' + f1(TT) + '">' +
    '<stop class="vcv-fs" offset="0"/><stop class="vcv-fs" offset="' + f2((H - 14) / TT) + '" stop-opacity=".94"/>' +
    '<stop class="vcv-fs" offset="' + f2((H + 30) / TT) + '" stop-opacity=".16"/><stop class="vcv-fs" offset="1" stop-opacity=".12"/></linearGradient></defs>' +
    '<rect width="' + WS + '" height="' + f1(TT) + '" fill="url(#' + p + 'fg)" clip-path="url(#' + p + 'fc)"/></svg>';

  // lace layer (static): net, folds, esprit dots, sheen, hem
  let s = svgOpen('vcv-lace', WS, TT) + '<defs>' + '<clipPath id="' + p + 'sil"><path d="' + silD + '"/></clipPath>' +
    netPattern(p + 'net', 4.6, 8, .42) + netPattern(p + 'gnet', 2.4, 4.2, .34) +
    // grillé: the denser half-stitch shading this lace lays behind its flowers
    netPattern(p + 'gril', 1.5, 2.6, .42) +
    '<pattern id="' + p + 'esp" width="23" height="20" patternUnits="userSpaceOnUse">' +
    '<ellipse class="vcv-t-fill" cx="5" cy="4" rx="1.05" ry=".9"/><ellipse class="vcv-t-fill" cx="16.5" cy="14" rx="1.05" ry=".9"/></pattern>' +
    // point d'esprit only in the lower 35% of the body, well clear of the type
    '<linearGradient id="' + p + 'eg" gradientUnits="userSpaceOnUse" x1="0" y1="' + f1(H * .65) + '" x2="0" y2="' + f1(H * .86) + '">' +
    '<stop offset="0" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#fff" stop-opacity=".8"/></linearGradient>' +
    '<mask id="' + p + 'em" maskUnits="userSpaceOnUse" x="0" y="0" width="' + WS + '" height="' + f1(TT) + '"><rect width="' + WS + '" height="' + f1(TT) + '" fill="url(#' + p + 'eg)"/></mask>' +
    // the lace's finer ground fades in from the tulle
    '<linearGradient id="' + p + 'bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity="0"/><stop offset=".3" stop-color="#fff" stop-opacity="1"/></linearGradient>' +
    '<mask id="' + p + 'bm" maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse" x="0" y="0" width="' + f1(W) + '" height="' + B + '"><rect width="' + f1(W) + '" height="' + B + '" fill="url(#' + p + 'bg)"/></mask>' +
    '</defs>';
  s += '<g clip-path="url(#' + p + 'sil)">' + foldImages(WS, TT, pal) +
    '<rect class="vcv-t-net" width="' + WS + '" height="' + f1(TT) + '" fill="url(#' + p + 'net)"/>' +
    '<rect width="' + WS + '" height="' + H + '" fill="url(#' + p + 'esp)" mask="url(#' + p + 'em)" opacity=".7"/></g>';

  // ---------- hem ----------
  s += '<g transform="translate(0 ' + (H - 1) + ') scale(' + K + ')">';
  let band = 'M0 0 L' + f1(W) + ' 0';
  for (let bx = Math.floor(W); bx >= 0; bx -= 1) band += ' L' + bx + ' ' + f1(outer(bx));
  band += 'Z';
  s += '<g mask="url(#' + p + 'bm)"><path d="' + band + '" fill="url(#' + p + 'gnet)"/></g>';

  let F = '';
  let C = '';
  let L = '';
  let D = '';
  let GR = '';   // toile fills, cordonnet, fine lines, dots, grillé
  // a few loose esprit dots where the lace ground begins
  for (let ex = 6; ex < W; ex += 11 + R() * 10) D += '<circle cx="' + f1(ex) + '" cy="' + f1(12 + R() * 22) + '" r=".62" opacity=".75"/>';

  // edge: outline, heavy cordonnet just inside, eyelets
  L += '<path stroke-width=".55" d="M0 ' + f1(outer(0)) + spline0(outer, W) + '"/>';
  C += '<path stroke-width="1.3" d="' + offsetPath(outer, W, -3.3) + '"/>';
  alongCurve(outer, 0, W, 8.6, (x, y) => {
    const u = (((x % P) + P) % P) / P;
    if (u < .07 || u > .93) return;
    L += '<circle stroke-width=".5" cx="' + f1(x) + '" cy="' + f1(y - 9.2) + '" r="1.25"/>';
  });
  // footing line inside the eyelets, and the grillé (dense half-stitch) border that swells under each spray: the shading this lace lays
  // along its scalloped edge
  const inner = (x) => outer(x) - 13.5;
  const gtop = (x) => { const u = (((x % P) + P) % P) / P; return outer(x) - 13.5 - (9 + 13 * Math.pow(Math.sin(Math.PI * u), 2)) - 1.6 * Math.sin(6 * Math.PI * u); };
  const gr = offsetPath(gtop, W, 0);
  let lowR = '';
  for (let gx = Math.floor(W); gx >= 0; gx -= 1) lowR += ' L' + gx + ' ' + f1(inner(gx));
  GR += '<path d="' + gr + lowR + 'Z"/>';
  L += '<path stroke-width=".5" d="' + offsetPath(inner, W, 0) + '"/>';
  L += '<path stroke-width=".5" opacity=".8" d="' + gr + '"/>';
  // picots: dots, a purl loop every third
  let cnt = 0;
  let PD = '';
  alongCurve(outer, 0, W, 4.6, (x, y, nx, ny) => {
    if (ny < 0) { nx = -nx; ny = -ny; }
    cnt++;
    if (cnt % 3 === 0) C += '<circle stroke-width=".55" cx="' + f1(x + nx * 2.9) + '" cy="' + f1(y + ny * 2.9) + '" r="1.2"/>';
    else PD += '<circle cx="' + f1(x + nx * 2.1) + '" cy="' + f1(y + ny * 2.1) + '" r=".78"/>';
  });

  for (let i = 0; i <= n; i++) {
    const x0 = i * P;
    const cy0 = outer(x0);
    // cusp: a three-dot trefoil
    PD += '<circle cx="' + f1(x0) + '" cy="' + f1(cy0 - 9) + '" r="1.05"/><circle cx="' + f1(x0 - 2.7) + '" cy="' + f1(cy0 - 13.4) + '" r=".95"/><circle cx="' + f1(x0 + 2.7) + '" cy="' + f1(cy0 - 13.4) + '" r=".95"/>';
    if (i === n) break;
    const xc = x0 + P / 2;
    const mir = i % 2 ? -1 : 1;
    const j = (a) => (R() - .5) * 2 * a;
    const g = { F: '', C: '', L: '', D: '' };
    // floral spray in scallop-local coordinates (x relative to the scallop centre)
    const rx = -6 + j(3);
    const ry = 60 + j(2);
    const rr = 19 * (1 + j(.05));
    const rot = j(18);
    const ro = rose(rx, ry, rr, rot, R);
    ro.outer.forEach((d) => { g.F += '<path d="' + d + '"/>'; g.C += '<path stroke-width="1.2" d="' + d + '"/>'; });
    ro.lip.forEach((d) => { g.L += '<path stroke-width=".6" d="' + d + '"/>'; });
    ro.inner.forEach((d) => { g.F += '<path d="' + d + '"/>'; g.C += '<path stroke-width="1" d="' + d + '"/>'; });
    g.L += '<path stroke-width=".75" d="' + ro.spiral + '"/>';
    const addLeaf = (b, ang, len, wid, bend) => {
      const lf = leafShape(b[0], b[1], ang + j(8), len * (1 + j(.1)), wid, bend);
      g.F += '<path d="' + lf.o + '"/>'; g.C += '<path stroke-width="1.05" d="' + lf.o + '"/>'; g.L += '<path stroke-width=".55" d="' + lf.m + '"/>';
      return lf;
    };
    const polS = (r, deg) => { const a = deg * Math.PI / 180; return [rx + r * Math.cos(a), ry + r * Math.sin(a)]; };
    const la = addLeaf(polS(rr * .88, 142), 154, 30, 5, .14);
    addLeaf(polS(rr * .88, -40), -30, 25, 4.6, -.16);
    addLeaf(polS(rr * .9, -118), -122, 18, 3.8, .14);
    // stem to a bud, with a small leaf on it
    const s0 = polS(rr * .97, 16);
    g.C += '<path stroke-width=".95" d="' + spline([s0, [27, 72 + j(2)], [44, 66 + j(2)], [55, 51]], false) + '"/>';
    addLeaf([35, 70.5], 66, 14, 3.4, .12);
    const bud = leafShape(55, 51.5, -64 + j(6), 12.5, 4.1, .05);
    g.F += '<path d="' + bud.o + '"/>'; g.C += '<path stroke-width="1" d="' + bud.o + '"/>';
    g.L += '<path stroke-width=".55" d="' + spline([[53.2, 52], [51, 47.2], [52, 43.8]], false) + '"/><path stroke-width=".55" d="' + spline([[56.8, 52.8], [61.4, 50.2], [64.2, 51.4]], false) + '"/>';
    // tendril curling up-left, ending in a small spiral
    const t0 = polS(rr * .97, -160);
    const tp = [t0, [-36, 50 + j(2)], [-47, 42], [-49, 35], [-45.8, 32.4], [-43, 34.6], [-44.4, 37]];
    g.L += '<path stroke-width=".7" d="' + spline(tp, false) + '"/>';
    // tiny sprig of three dots off the lower leaf
    g.D += '<circle cx="' + f1(la.tip[0] - 4) + '" cy="' + f1(la.tip[1] + 2) + '" r=".85"/><circle cx="' + f1(la.tip[0] - 7.6) + '" cy="' + f1(la.tip[1] - .6) + '" r=".65"/><circle cx="' + f1(la.tip[0] - 1.8) + '" cy="' + f1(la.tip[1] + 5.6) + '" r=".65"/>';
    const tf = 'translate(' + f1(xc) + ' 0) scale(' + mir + ' 1)';
    F += '<g transform="' + tf + '">' + g.F + '</g>';
    C += '<g transform="' + tf + '">' + g.C + '</g>';
    L += '<g transform="' + tf + '">' + g.L + '</g>';
    D += '<g transform="' + tf + '">' + g.D + '</g>';
  }
  s += '<g fill="url(#' + p + 'gril)" opacity=".9">' + GR + '</g>';
  s += '<g class="vcv-t-deep">' + F + '</g>';
  s += '<g class="vcv-t-line">' + L + '</g>';
  s += '<g class="vcv-t-fill">' + D + '</g>';
  // the cordonnet and picots sit proud of the ground: a 0.5px shadow beneath (ivory) or a candle-lit rim above (black)
  s += '<g class="vcv-t-shade"><g class="vcv-t-hl">' + C + '</g><g class="vcv-t-hlfill">' + PD + '</g></g>';
  s += '<g class="vcv-t-cord">' + C + '</g><g class="vcv-t-cordfill">' + PD + '</g>';
  s += '</g></svg>';
  return { fill, lace: s, total: TT };
}

/* BACK VEIL, lace.html:634-652: plain tulle, no hem: a soft raw edge feathered over 24px, so there is one hem and one focal point. */
function buildBack(WS, H, pal) {
  const p = 'vcvB';
  const TT = H + 48;
  const R = rng(911);
  const ph = R() * 6;
  const wave = (x) => TT - 10 + 6 * Math.sin(x / 190 + ph) + 3 * Math.sin(x / 67 + ph * 2);
  let silD = 'M0 0 L' + WS + ' 0';
  for (let x = WS; x >= 0; x -= 4) silD += ' L' + x + ' ' + f1(wave(x));
  silD += 'Z';
  // the two layers are separate SVGs of one document: their ids differ by `sfx` (the prototype's 'x')
  const feather = (sfx) => '<linearGradient id="' + p + 'ff' + sfx + '" gradientUnits="userSpaceOnUse" x1="0" y1="' + (TT - 40) + '" x2="0" y2="' + (TT - 16) + '">' +
    '<stop offset="0" stop-color="#fff"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></linearGradient>' +
    '<mask id="' + p + 'fm' + sfx + '" maskUnits="userSpaceOnUse" x="0" y="0" width="' + WS + '" height="' + TT + '"><rect width="' + WS + '" height="' + TT + '" fill="url(#' + p + 'ff' + sfx + ')"/></mask>' +
    '<clipPath id="' + p + 'c' + sfx + '"><path d="' + silD + '"/></clipPath>';
  const fill = svgOpen('vcv-tulle', WS, TT) + '<defs>' + feather('x') + '</defs>' +
    '<g clip-path="url(#' + p + 'cx)" mask="url(#' + p + 'fmx)"><rect width="' + WS + '" height="' + TT + '" style="fill:var(--vcv-fill)"/></g></svg>';
  const lace = svgOpen('vcv-lace', WS, TT) + '<defs>' + feather('') + netPattern(p + 'net', 4.4, 7.6, .36) + '</defs>' +
    '<g clip-path="url(#' + p + 'c)" mask="url(#' + p + 'fm)">' + foldImages(WS, TT, pal) +
    '<rect class="vcv-t-net" width="' + WS + '" height="' + TT + '" fill="url(#' + p + 'net)"/></g></svg>';
  return { fill, lace, total: TT };
}

/* ---- the two halves of the choreography, lace.html:670-680 ----
   The prototype's TL: back tulle down 0-820 and up 1280-1960, front lace down 80-900 and up 1160-1880 (the front lifts first).
   cover() there is 0 (above the screen) .. 1 (covering). The descent runs 0-900 of it and the lift 1160-1960, each scaled to the brief's
   650 ms (DS, LS) with the offset between the two veils kept. */
const DS = DESCEND_MS / 900;
const LS = LIFT_MS / 800;
function coverAt(dir, t) {
  if (dir === 'down') return { cb: easeDown(seg(t, 0, 820 * DS)), cf: easeDown(seg(t, 80 * DS, 900 * DS)) };
  return { cb: 1 - easeUp(seg(t, 120 * LS, 800 * LS)), cf: 1 - easeUp(seg(t, 0, 720 * LS)) };
}

/* ---- the veil on the page ---- */
let veil = null;      // { root, backEl, frontEl, fillB, fillF, gb, gf, H }
let state = 'idle';   // idle | leaving (descending, or waiting for the browser to leave) | lifting (the arrival)
let cancelPlay = () => {};
let failT = 0;
let stuckT = 0;
let dest = '';        // the href the departure in progress is taking the browser to (the newest veil-able link clicked)
let landed = false;   // the descent is over and location.assign has been called: the browser is leaving, or refused to

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const store = {
  get() { try { return sessionStorage.getItem(KEY); } catch (_) { return null; } },
  set(v) { try { sessionStorage.setItem(KEY, v); } catch (_) { /* storage blocked: the next page simply starts uncovered */ } },
  clear() { try { sessionStorage.removeItem(KEY); } catch (_) { /* nothing to clear */ } },
};

/** Build the veil for a target mood (the palette is the target's) and append it, above the screen. */
function mount(mood) {
  const pal = mood === 'sweet' ? 'ivory' : 'black';
  const root = document.createElement('div');
  root.className = 'vc-veil vc-veil--' + pal;
  root.setAttribute('aria-hidden', 'true');
  root.innerHTML = '<div class="vc-veil__layer vc-veil__back"></div><div class="vc-veil__layer vc-veil__front"></div>';
  document.body.appendChild(root);
  const [backEl, frontEl] = root.children;
  const W = Math.round(root.clientWidth) + 48;
  const H = Math.round(root.clientHeight);
  const F = buildFront(W, H, pal);
  const B = buildBack(W, H, pal);
  frontEl.innerHTML = F.fill + F.lace;
  backEl.innerHTML = B.fill + B.lace;
  frontEl.style.height = f1(F.total) + 'px';
  backEl.style.height = f1(B.total) + 'px';
  veil = { root, backEl, frontEl, fillB: backEl.querySelector('.vcv-tulle'), fillF: frontEl.querySelector('.vcv-tulle'), gb: B.total, gf: F.total, H };
  return veil;
}

/** lace.html:696-711: the front veil stops with its hem resting on the floor of the screen; the back tulle covers fully. Sheer while
 *  travelling, gathered to near-opaque as it covers. cb / cf: how far the back / front veil is down, 0 .. 1. */
function paint(cb, cf) {
  const v = veil;
  const yb = -v.gb + cb * v.gb;
  const yf = -v.gf + cf * v.H;
  const xb = -5 * Math.sin(Math.PI * cb);
  const xf = 7 * Math.sin(Math.PI * cf);
  const sk = .6 * Math.sin(Math.PI * cf);
  v.backEl.style.transform = 'translate3d(' + f1(xb) + 'px,' + f1(yb) + 'px,0)';
  v.frontEl.style.transform = 'translate3d(' + f1(xf) + 'px,' + f1(yf) + 'px,0) skewX(' + sk.toFixed(3) + 'deg)';
  v.fillF.style.opacity = (.10 + .72 * cf * cf).toFixed(3);
  v.fillB.style.opacity = (.06 + .3 * cb * cb).toFixed(3);
}

/** Run one half; `done` is called once, also when no animation frame ever comes (a hidden tab): the backstop timer. Starting a half
 *  stops the one before it (a bfcache restore must not let an abandoned descent go on and navigate). */
function play(dir, ms, done) {
  cancelPlay();
  let over = false;
  let raf = 0;
  const end = () => { if (over) return; over = true; cancelAnimationFrame(raf); clearTimeout(backstop); done(); };
  const backstop = setTimeout(end, ms + 400);
  cancelPlay = () => { over = true; cancelAnimationFrame(raf); clearTimeout(backstop); };
  const t0 = performance.now();
  const frame = (now) => {
    if (over || !veil) return;
    const t = Math.min(ms, now - t0);
    const c = coverAt(dir, t);
    paint(c.cb, c.cf);
    if (t < ms) raf = requestAnimationFrame(frame); else end();
  };
  frame(t0);
}

/** The curtain is gone, in whatever state it was: the root, the class, the attributes, the key, the timers. Idempotent. */
function dismiss() {
  cancelPlay();
  clearTimeout(failT);
  clearTimeout(stuckT);
  document.querySelectorAll('.vc-veil').forEach((n) => n.remove());
  veil = null;
  const de = document.documentElement;
  de.classList.remove('vc-veiled');
  de.removeAttribute('data-vc-veil');
  de.removeAttribute('data-vc-lift');
  store.clear();
  dest = '';
  landed = false;
  state = 'idle';
}

/** Lift what is on the page: the veil rises (and the sheet the stylesheet drew fades with it), then everything goes. */
function lift() {
  if (!veil) return;   // the fail-safe (or a failed build) got there first
  state = 'lifting';
  document.documentElement.setAttribute('data-vc-lift', '');
  play('up', LIFT_MS, dismiss);
}

/** The DOM is ready and has painted: DOMContentLoaded, then two frames (the first is when the browser paints what the parser made). */
function afterFirstPaint(fn) {
  let go = false;
  const start = () => { if (go) return; go = true; requestAnimationFrame(() => requestAnimationFrame(fn)); };
  if (document.readyState === 'complete') start();
  else {
    document.addEventListener('DOMContentLoaded', start, { once: true });
    window.addEventListener('load', start, { once: true });
  }
}

/** A veiled page: the veil in the covered pose over the stylesheet's sheet, the hard timer, and the lift after the first paint. */
function arrive(mood) {
  state = 'lifting';
  try {
    mount(mood);
    paint(1, 1);
  } catch (err) { dismiss(); return; }
  failT = setTimeout(dismiss, FAILSAFE_MS);
  afterFirstPaint(lift);
}

/** The browser has been sent to `dest` and the page has not left: if it is still here STUCK_MS later (the navigation was refused or
 *  aborted), the veil lifts and everything goes. Started again by every new location.assign. */
function armStuck() {
  clearTimeout(stuckT);
  stuckT = setTimeout(() => { if (state === 'leaving') { failT = setTimeout(dismiss, FAILSAFE_MS); lift(); } }, STUCK_MS);
}

/** The click was taken: the veil comes down, then the browser goes to the link. */
function depart(href, mood) {
  state = 'leaving';
  dest = href;
  landed = false;
  try {
    mount(mood);
    paint(0, 0);
  } catch (err) { dismiss(); location.assign(href); return; }   // no veil: the link works as it would have
  play('down', DESCEND_MS, () => {
    store.set(mood);
    landed = true;
    armStuck();
    location.assign(dest);   // the newest link: a later click may have retargeted the descent
  });
}

/** A veil-able click while a departure is under way (batch review 1, M1): it is not swallowed, the departure restarts to the NEW link. The
 *  mood is the same as the first link's (a link is veiled only when its mood is not the page's, and there are two moods), so the veil
 *  already drawn has the right palette. The same link again (a double click) changes nothing. */
function retarget(href, mood) {
  if (href === dest) return;
  dest = href;
  if (!landed) return;       // the curtain is still coming down: it lands on the newest link
  store.set(mood);
  armStuck();
  location.assign(href);     // the browser was leaving for the first link (or refused to): the new one replaces it
}

function onClick(e) {
  const a = e.target instanceof Element ? e.target.closest('a[data-mood]') : null;
  if (!a) return;
  const mood = veilTarget(e, { mood: a.dataset.mood, href: a.href, attr: a.getAttribute('href'), target: a.target, download: a.hasAttribute('download') },
    { href: location.href, mood: document.documentElement.dataset.mood, reducedMotion: reduced() });
  if (!mood || state === 'lifting') return;   // while the arrival's veil is still rising, the browser follows the link and no second curtain falls
  e.preventDefault();
  if (state === 'leaving') { retarget(a.href, mood); return; }
  depart(a.href, mood);
}

/* A page restored from the back/forward cache is as it was left: the veil down over it. Lift it at once (nothing is loading), and drop
   the key, which belonged to the navigation we left. */
function onPageShow(e) {
  if (!e.persisted) return;
  clearTimeout(stuckT);
  store.clear();
  if (!veil) { state = 'idle'; return; }
  failT = setTimeout(dismiss, FAILSAFE_MS);
  lift();
}

document.addEventListener('click', onClick);
window.addEventListener('pageshow', onPageShow);

const html = document.documentElement;
const arrived = html.getAttribute('data-vc-veil');
if (html.classList.contains('vc-veiled') && (arrived === 'noir' || arrived === 'sweet') && !reduced()) arrive(arrived);
else dismiss();   // nothing to lift: no class, or the head script's class under reduced motion
